'use client';

import moment from 'moment';
import { useState } from 'react';
import renderHTML from 'react-render-html';
import { Link } from 'react-router-dom';
import { URI } from '../../../domain/constant';
import useAuthUser from '../../../hooks/useAuthUser';

import defaultProfilePic from '../../../assets/upload.jpeg';

function EnhancedProfileSingleView({
  data,
  inputFields,
  label,
  link,
  id,
  hideGallery,
  hideAllBtn,
  col,
  totalEstimateTime,
  children,
}) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const { user, isAuthenticated, loading } = useAuthUser();

  const renderFieldValue = (item, inputField, data) => {
    if (inputField.inputType === 'date') {
      return moment(data[item]).format('DD-MMM-YYYY');
    }
    if (inputField.inputType === 'datetime-local') {
      return moment(data[item]).utc().format('DD-MMM-YYYY hh:mm:ss A');
    }
    if (inputField.type === 'checkbox') {
      return (
        <span
          className={`enhanced-badge ${
            data[item] ? 'badge-success' : 'badge-danger'
          }`}
        >
          {data[item] ? 'YES' : 'NO'}
        </span>
      );
    }
    if (inputField.type === 'related') {
      if (inputField.multiple) {
        return data[item]?.map((e, index) => (
          <span key={index} className='enhanced-badge badge-info'>
            {!inputField.static ? e[inputField.field] : e}
          </span>
        ));
      } else {
        return data[item]
          ? !inputField.static
            ? data[item][inputField.field]
            : data[item]
          : '-';
      }
    }
    if (inputField.type === 'html') {
      return data[item] && renderHTML(data[item]);
    }
    if (inputField.type === 'file') {
      return (
        data[item] && (
          <a
            href={`${URI}${data[item]}`}
            target='_blank'
            className='enhanced-file-link'
            rel='noreferrer'
          >
            <i className='fa fa-download me-1'></i>
            View File
          </a>
        )
      );
    }
    if (inputField.type === 'gallery') {
      return data[item]?.map((pic, index) => (
        <div key={index} className='enhanced-gallery-item'>
          {!hideGallery ? (
            <img
              src={`${URI}${pic.image}`}
              className='enhanced-gallery-image'
              alt='Gallery item'
            />
          ) : (
            <a
              href={`${URI}${pic.image}`}
              target='_blank'
              className='enhanced-file-link'
              rel='noreferrer'
            >
              <i className='fa fa-image me-1'></i>
              View Image
            </a>
          )}
        </div>
      ));
    }
    return data[item];
  };

  const renderField = (item, index) => {
    const inputField = inputFields[item];

    if (!inputField || inputField.hideOnView) return null;

    // Skip fields that are already shown in the profile header
    const profileFields = [
      'name',
      'designation',
      'employee_status',
      'reporting_manager',
      'email',
      'phone',
      'emergency_contact_no',
      'image',
    ];
    if (profileFields.includes(item)) return null;

    if (inputField.type === 'divider') {
      return (
        <div key={index} className='col-12 mb-3'>
          <div className='enhanced-divider'>
            <h6 className='enhanced-divider-title'>{inputField.title}</h6>
          </div>
        </div>
      );
    }

    return (
      <div key={index} className='col-6 mb-3'>
        <div className='enhanced-info-field'>
          <div className='enhanced-field-label'>
            {inputField.title.toUpperCase()}:
          </div>
          <div className='enhanced-field-value'>
            {renderFieldValue(item, inputField, data)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className='enhanced-profile-container'>
        <div className='enhanced-profile-row'>
          <div
            className={`enhanced-main-content ${
              sidebarCollapsed ? 'expanded' : ''
            }`}
          >
            {/* Top Header Bar */}
            <div className='enhanced-top-header'>
              <div className='enhanced-header-left'>
                <button
                  className='enhanced-sidebar-toggle'
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  title={sidebarCollapsed ? 'Show Sidebar' : 'Hide Sidebar'}
                >
                  <i
                    className={`fa ${
                      sidebarCollapsed ? 'fa-chevron-left' : 'fa-chevron-right'
                    }`}
                  ></i>
                </button>
              </div>
              <div className='enhanced-header-right'>
                {!hideAllBtn &&
                  isAuthenticated &&
                  user &&
                  user.role === 'SUPER ADMIN' && (
                    <Link
                      to={`/${link}`}
                      className='enhanced-header-btn enhanced-btn-back'
                    >
                      <i className='fa fa-angle-left me-1'></i>
                      Back to {label}
                    </Link>
                  )}
                {/* Only show Edit if user is viewing their own profile or is SUPER ADMIN */}
                {isAuthenticated && user && user.role === 'SUPER ADMIN' && (
                  <Link
                    to={`/${link}/${id}/edit`}
                    className='enhanced-header-btn enhanced-btn-edit'
                  >
                    <i className='fa fa-edit me-1'></i>
                    Edit
                  </Link>
                )}
              </div>
            </div>

            {/* Main Profile Card */}
            <div className='enhanced-profile-card'>
              {/* Profile Section - Left Half */}
              <div className='enhanced-profile-section'>
                <div className='enhanced-profile-content'>
                  <div className='enhanced-profile-image'>
                    <img
                      src={
                        data.image ? `${URI}${data.image}` : defaultProfilePic
                      }
                      alt={data.name || 'Profile'}
                    />
                  </div>

                  <h1 className='enhanced-profile-name'>{data.name}</h1>

                  <div className='enhanced-profile-info'>
                    <div className='enhanced-info-item'>
                      <i className='fa fa-briefcase'></i>
                      <span>{data.designation}</span>
                    </div>

                    <div className='enhanced-info-item'>
                      <i className='fa fa-circle'></i>
                      <span
                        className={`enhanced-status ${data.employee_status?.toLowerCase()}`}
                      >
                        {data.employee_status}
                      </span>
                    </div>

                    {data.reporting_manager && (
                      <div className='enhanced-info-item'>
                        <i className='fa fa-user'></i>
                        <span>Reports to: {data.reporting_manager.name}</span>
                      </div>
                    )}

                    <div className='enhanced-info-item'>
                      <i className='fa fa-envelope'></i>
                      <a href={`mailto:${data.email}`}>{data.email}</a>
                    </div>

                    <div className='enhanced-info-item'>
                      <i className='fa fa-phone'></i>
                      <a href={`tel:${data.phone}`}>{data.phone}</a>
                    </div>

                    {data.emergency_contact_no && (
                      <div className='enhanced-info-item'>
                        <i className='fa fa-exclamation-triangle'></i>
                        <span>{data.emergency_contact_no}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Additional Information Section - Right Half */}
              <div className='enhanced-details-section'>
                <div className='enhanced-details-header'>
                  <h2 className='enhanced-section-title'>
                    Additional Information
                  </h2>
                </div>

                <div className='enhanced-details-content'>
                  <div className='row'>
                    {inputFields &&
                      Object.keys(inputFields).map((item, index) =>
                        renderField(item, index)
                      )}

                    {/* System Fields */}
                    {data.created_by && (
                      <div className='col-6 mb-3'>
                        <div className='enhanced-info-field'>
                          <div className='enhanced-field-label'>
                            CREATED BY:
                          </div>
                          <div className='enhanced-field-value'>
                            {data.created_by.name}
                          </div>
                        </div>
                      </div>
                    )}

                    {data.updated_by && (
                      <div className='col-6 mb-3'>
                        <div className='enhanced-info-field'>
                          <div className='enhanced-field-label'>
                            UPDATED BY:
                          </div>
                          <div className='enhanced-field-value'>
                            {data.updated_by.name}
                          </div>
                        </div>
                      </div>
                    )}

                    {data.createdAt && (
                      <div className='col-6 mb-3'>
                        <div className='enhanced-info-field'>
                          <div className='enhanced-field-label'>
                            CREATED AT:
                          </div>
                          <div className='enhanced-field-value'>
                            {moment(data.createdAt).format(
                              'DD-MM-YYYY hh:mm A'
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {data.updatedAt && (
                      <div className='col-6 mb-3'>
                        <div className='enhanced-info-field'>
                          <div className='enhanced-field-label'>
                            UPDATED AT:
                          </div>
                          <div className='enhanced-field-value'>
                            {moment(data.updatedAt).format(
                              'DD-MM-YYYY hh:mm A'
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          {!sidebarCollapsed && (
            <div className='enhanced-sidebar'>
              <div className='enhanced-sidebar-content'>{children}</div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .enhanced-profile-container {
          background-color: #f8f9fa;
          min-height: 100vh;
          padding: 0;
        }

        .enhanced-profile-row {
          display: flex;
          max-width: 100%;
          margin: 0;
        }

        .enhanced-main-content {
          flex: ${col ? `0 0 ${(col / 12) * 100}%` : '0 0 75%'};
          transition: all 0.3s ease;
        }

        .enhanced-main-content.expanded {
          flex: 1;
        }

        .enhanced-sidebar {
          flex: ${col ? `0 0 ${((12 - col) / 12) * 100}%` : '0 0 25%'};
          transition: all 0.3s ease;
          background: white;
          border-left: 1px solid #e9ecef;
        }

        .enhanced-sidebar-content {
          padding: 20px;
          height: 100vh;
          overflow-y: auto;
        }

        .enhanced-top-header {
          background: linear-gradient(
            135deg,
            #1f2937 0%,
            #374151 30%,
            #dc2626 70%,
            #b91c1c 100%
          );
          padding: 12px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .enhanced-header-left {
          display: flex;
          align-items: center;
        }

        .enhanced-header-right {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .enhanced-sidebar-toggle {
          background: rgba(255, 255, 255, 0.15);
          border: none;
          color: white;
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .enhanced-sidebar-toggle:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-1px);
        }

        .enhanced-header-btn {
          padding: 8px 16px;
          border-radius: 6px;
          text-decoration: none;
          font-weight: 500;
          font-size: 13px;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .enhanced-btn-back {
          background: rgba(255, 255, 255, 0.1);
          color: white;
        }

        .enhanced-btn-back:hover {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          transform: translateY(-1px);
        }

        .enhanced-btn-edit {
          background: #28a745;
          color: white;
          border-color: #28a745;
        }

        .enhanced-btn-edit:hover {
          background: #218838;
          border-color: #218838;
          color: white;
          transform: translateY(-1px);
        }

        .enhanced-profile-card {
          display: flex;
          min-height: calc(75vh - 45px); /* Reduced from calc(100vh - 60px) */
          background: white;
        }

        .enhanced-profile-section {
          flex: 1;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

          background: linear-gradient(
            135deg,
            #1f2937 0%,
            #374151 30%,
            #dc2626 70%,
            #b91c1c 100%
          );

          color: white;
          padding: 25px 20px; /* Reduced from 40px 30px */
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .enhanced-profile-content {
          text-align: center;
          max-width: 300px;
          width: 100%;
        }

        .enhanced-profile-image {
          margin-bottom: 25px;
        }

        .enhanced-profile-image img {
          width: 100px; /* Reduced from 120px */
          height: 100px; /* Reduced from 120px */
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid rgba(255, 255, 255, 0.2); /* Reduced from 4px */
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1); /* Reduced shadow */
        }

        .enhanced-profile-name {
          font-size: 26px; /* Reduced from 32px */
          font-weight: 700;
          margin: 0 0 20px 0; /* Reduced from 30px */
          color: white;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .enhanced-profile-info {
          display: flex;
          flex-direction: column;
          gap: 10px; /* Reduced from 15px */
          text-align: left;
        }

        .enhanced-info-item {
          display: flex;
          align-items: center;
          gap: 10px; /* Reduced from 12px */
          font-size: 13px; /* Reduced from 14px */
          color: rgba(255, 255, 255, 0.95);
          padding: 6px 0; /* Reduced from 8px */
        }

        .enhanced-info-item i {
          width: 18px;
          text-align: center;
          opacity: 0.8;
        }

        .enhanced-info-item a {
          color: rgba(255, 255, 255, 0.95);
          text-decoration: none;
        }

        .enhanced-info-item a:hover {
          color: white;
          text-decoration: underline;
        }

        .enhanced-status {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .enhanced-status.working {
          background: rgba(40, 167, 69, 0.2);
          color: #28a745;
          border: 1px solid rgba(40, 167, 69, 0.3);
        }

        .enhanced-status.inactive {
          background: rgba(220, 53, 69, 0.2);
          color: #dc3545;
          border: 1px solid rgba(220, 53, 69, 0.3);
        }

        .enhanced-details-section {
          flex: 1;
          background: #f8f9fa;
          display: flex;
          flex-direction: column;
        }

        .enhanced-details-header {
          background: #e9ecef;
          padding: 15px 20px; /* Reduced from 20px 25px */
          border-bottom: 1px solid #dee2e6;
        }

        .enhanced-section-title {
          font-size: 18px;
          font-weight: 600;
          color: #495057;
          margin: 0;
        }

        .enhanced-details-content {
          padding: 20px; /* Reduced from 25px */
          flex: 1;
          overflow-y: auto;
        }

        .enhanced-info-field {
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          padding: 10px 12px; /* Reduced from 12px 15px */
          transition: all 0.2s ease;
          height: 100%;
        }

        .enhanced-info-field:hover {
          border-color: #dee2e6;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .enhanced-field-label {
          font-size: 11px;
          font-weight: 600;
          color: #6c757d;
          margin-bottom: 4px;
          letter-spacing: 0.5px;
        }

        .enhanced-field-value {
          font-size: 14px;
          font-weight: 500;
          color: #212529;
          line-height: 1.4;
          word-wrap: break-word;
        }

        .enhanced-divider {
          background: linear-gradient(135deg, #e9ecef 0%, #f8f9fa 100%);
          border-radius: 6px;
          padding: 12px 16px;
          margin: 10px 0;
          border-left: 4px solid #667eea;
        }

        .enhanced-divider-title {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #495057;
        }

        .enhanced-badge {
          display: inline-block;
          padding: 3px 8px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          margin: 2px;
        }

        .badge-success {
          background: #d4edda;
          color: #155724;
        }

        .badge-danger {
          background: #f8d7da;
          color: #721c24;
        }

        .badge-info {
          background: #d1ecf1;
          color: #0c5460;
        }

        .enhanced-file-link {
          color: #007bff;
          text-decoration: none;
          font-weight: 500;
          font-size: 12px;
          display: inline-flex;
          align-items: center;
        }

        .enhanced-file-link:hover {
          color: #0056b3;
          text-decoration: underline;
        }

        .enhanced-gallery-item {
          margin: 5px 0;
        }

        .enhanced-gallery-image {
          width: 100%;
          max-width: 150px;
          height: 80px;
          object-fit: cover;
          border-radius: 4px;
          border: 1px solid #dee2e6;
        }

        @media (max-width: 768px) {
          .enhanced-profile-row {
            flex-direction: column;
          }

          .enhanced-main-content,
          .enhanced-sidebar {
            flex: 1;
          }

          .enhanced-profile-card {
            flex-direction: column;
            min-height: auto;
          }

          .enhanced-profile-section {
            padding: 30px 20px;
          }

          .enhanced-profile-name {
            font-size: 24px;
          }

          .enhanced-details-content .row .col-6 {
            flex: 0 0 100%;
            max-width: 100%;
            margin-bottom: 15px; /* Reduced from default spacing */
          }

          .enhanced-top-header {
            flex-direction: column;
            gap: 10px;
            align-items: stretch;
          }

          .enhanced-header-right {
            justify-content: center;
          }
        }
      `}</style>
    </>
  );
}

export default EnhancedProfileSingleView;
